import { useState } from 'react';

import { CareerCategory, CareerPath } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { FIT_SCORE_LEVEL_MAP } from '@backrs_ui/grow-ui/src/pages/CareerPath/util';

import { Box, Typography, SxProps, Theme, Button } from '@mui/material';

import { BlueprintStrongFitPath } from './BlueprintStrongFitPath';
import { NoBlueprintStrongFitPaths } from './NoBlueprintStrongtFitPaths';
import { useAgentContext } from './StrongFitPaths.agentContext';
import { WhatIsACareerPathModal } from './WhatIsACareerPathModal';

const STRINGS = appStrings.modules.careerPath.summaryPage.strongFitPaths;

type StrongFitPathsProps = Readonly<{
  careerCategories: CareerCategory[];
  careerPaths: CareerPath[];
  careerPathSuggestions: CareerPathWithScore[] | undefined;
  userHasBlueprint: boolean;
  isShared?: boolean;
}>;

export const StrongFitPaths = ({
  careerCategories,
  careerPaths,
  careerPathSuggestions,
  userHasBlueprint,
  isShared = false,
}: StrongFitPathsProps) => {
  useAgentContext(careerPathSuggestions, FIT_SCORE_LEVEL_MAP);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // If no career paths, don't render the section
  if (careerPaths.length === 0) {
    return null;
  }

  return (
    <Box sx={containerStyle}>
      <Box sx={headerContainerSx}>
        <Box>
          <Typography variant="text-lg-semibold">
            {userHasBlueprint ? STRINGS.title : STRINGS.noBlueprintTitle}
          </Typography>

          <Typography
            variant="text-sm-regular"
            color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          >
            {STRINGS.description}
          </Typography>
        </Box>
        <Button
          id="career-path-what-is-career-path-button"
          variant="contained"
          size="md"
          hierarchy="Secondary gray"
          sx={whatIsCareerPathSx}
          onClick={() => setIsModalOpen(true)}
        >
          <Typography variant="text-sm-semibold">{STRINGS.whatIsACareerPath}</Typography>
        </Button>
      </Box>

      {userHasBlueprint ? (
        <BlueprintStrongFitPath
          careerPathSuggestions={careerPathSuggestions}
          careerCategories={careerCategories}
          careerPaths={careerPaths}
          isShared={isShared}
        />
      ) : (
        <NoBlueprintStrongFitPaths />
      )}

      <WhatIsACareerPathModal
        careerPaths={careerPaths}
        careerCategories={careerCategories}
        open={isModalOpen}
        onClose={handleModalClose}
      />
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
} as const;

const whatIsCareerPathSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  mr: backrs.spacing['spacing-xs'],
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});
